import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  axios,
  CompanyBenefit,
  CompanyBenefitService,
  CompanyEmployeeService,
  EmployeeToken,
  EmployeeTokenService,
} from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { Storage } from '../../constants';
import { useDependencies } from '../../dependencies';

const hashEmail = (email?: string): string => {
  if (!email) return '';
  const at = email.lastIndexOf('@');
  if (at < 1) return email;
  const name = email.substring(0, at);
  const domain = email.substring(at);
  return `${name[0]}****${name[name.length - 1]}${domain}`;
};

export const ConfirmBenefits = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { stores } = useDependencies();
  const user = stores.user.user;

  const [benefits, setBenefits] = useState<CompanyBenefit | null>(null);
  const [employeeData, setEmployeeData] = useState<EmployeeToken | null>(null);
  const [showResend, setShowResend] = useState(false);
  const [resending, setResending] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'UrbanSitter - Confirm Benefits';
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    const readEmployeeData = (): EmployeeToken | null => {
      const raw = localStorage.getItem(Storage.COMPANY_EMPLOYEE);
      if (!raw) return null;
      try {
        return JSON.parse(raw) as EmployeeToken;
      } catch {
        return null;
      }
    };

    const addSkipCookie = (): void => {
      const expires = new Date();
      expires.setMinutes(expires.getMinutes() + 60);
      document.cookie = `${Storage.SKIP_BENEFITS_CHECK}=true; expires=${expires.toUTCString()}; path=/`;
    };

    const loadCompanyBenefits = async (companyId: number | string): Promise<void> => {
      const nowYmd = new Date().toISOString().slice(0, 10);
      try {
        const resp = await CompanyBenefitService.getByFilter({
          params: {
            companyId,
            q: `end.gt(date.valueOf('${nowYmd}'))`,
            statusId: 2,
          },
        });
        if (cancelled) return;
        const list = (resp.data?.data ?? []) as CompanyBenefit[];
        if (list.length) {
          setBenefits(list[0]);
        } else {
          addSkipCookie();
          toast.error(t('pages.confirmBenefits.unableToLocate'));
          setTimeout(() => navigate('/family/dashboard'), 4000);
        }
      } catch (err) {
        console.warn('[ConfirmBenefits] benefits load failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const triggerEmail = async (companyEmail: string): Promise<void> => {
      try {
        const resp = await EmployeeTokenService.create({ email: companyEmail, userId: user.id! });
        if (cancelled) return;
        const d = resp.data?.data ?? {};
        if (!d.companyId) {
          toast.error(t('pages.confirmBenefits.noPendingBenefits'));
          setTimeout(() => navigate('/family/dashboard'), 4000);
          setLoading(false);
          return;
        }
        const nextData: EmployeeToken = {
          id: d.id,
          companyId: d.companyId,
          email: d.email,
        };
        setEmployeeData(nextData);
        localStorage.setItem(Storage.COMPANY_EMPLOYEE, JSON.stringify(nextData));
        localStorage.removeItem(Storage.COMPANY_EMAIL);
        void loadCompanyBenefits(nextData.companyId!);
        setTimeout(() => {
          if (!cancelled) setShowResend(true);
        }, 15000);
      } catch (err) {
        console.warn('[ConfirmBenefits] triggerEmail failed', err);
        setLoading(false);
      }
    };

    const companyEmail = localStorage.getItem(Storage.COMPANY_EMAIL);
    const companyValidation = localStorage.getItem(Storage.COMPANY_VALIDATION) !== 'false';
    const existingEmployee = readEmployeeData();

    if (companyEmail) {
      if (!companyValidation) {
        navigate('/activate-benefits', { replace: true });
      } else {
        void triggerEmail(companyEmail);
      }
    } else if (existingEmployee?.companyId) {
      setEmployeeData(existingEmployee);
      setShowResend(true);
      void loadCompanyBenefits(existingEmployee.companyId);
    } else {
      void (async (): Promise<void> => {
        try {
          const resp = await CompanyEmployeeService.findByFilter({ params: { userId: user.id } });
          if (cancelled) return;
          const list = resp.data?.data ?? [];
          const match = list.find((e) => e.userId === user.id);
          if (match) {
            localStorage.removeItem(Storage.COMPANY_EMPLOYEE);
            toast.success(t('pages.confirmBenefits.alreadyActivated'));
            setTimeout(() => navigate('/family/dashboard'), 4000);
            setLoading(false);
            return;
          }
          addSkipCookie();
          toast.error(t('pages.confirmBenefits.noPendingBenefits'));
          setTimeout(() => navigate('/family/dashboard'), 4000);
          setLoading(false);
        } catch {
          if (cancelled) return;
          addSkipCookie();
          toast.error(t('pages.confirmBenefits.noBenefitsAvailable'));
          setTimeout(() => navigate('/family/dashboard'), 4000);
          setLoading(false);
        }
      })();
    }

    return (): void => {
      cancelled = true;
    };
  }, [user?.id, navigate, t]);

  const resendEmail = async (): Promise<void> => {
    const raw = localStorage.getItem(Storage.COMPANY_EMPLOYEE);
    const data = raw ? (JSON.parse(raw) as EmployeeToken) : null;

    if (!data || !user?.id) {
      toast.success(t('pages.confirmBenefits.alreadyActivated'));
      setTimeout(() => navigate('/family/dashboard'), 4000);
      return;
    }

    setResending(true);
    try {
      await EmployeeTokenService.patch({
        id: data.id,
        email: data.email,
        userId: user.id,
      });
      toast.success(t('pages.confirmBenefits.verificationSent'));
      setTimeout(() => setResending(false), 10000);
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.data?.errors?.[0]?.message) {
        toast.error(e.response.data.errors[0].message);
      } else {
        toast.error(t('pages.confirmBenefits.resendFailed'));
      }
      setResending(false);
    }
  };

  if (loading) {
    return (
      <div className='container py-8 text-center'>
        <p className='text-neutral-600'>{t('common.loading')}</p>
      </div>
    );
  }

  if (!benefits) {
    return (
      <div className='container py-8 text-center'>
        <p className='text-neutral-600'>{t('pages.confirmBenefits.unableToLocate')}</p>
      </div>
    );
  }

  return (
    <div className='container py-12 max-w-xl mx-auto'>
      <div className='us-card rounded p-6'>
        <h1 className='text-2xl font-semibold mb-4'>{t('pages.confirmBenefits.heading')}</h1>

        {benefits.company?.logoUrl && (
          <div className='text-center mb-6'>
            <img src={benefits.company.logoUrl} alt={benefits.company.name ?? ''} className='inline-block max-h-24' />
          </div>
        )}

        <p className='font-semibold mb-2'>{t('pages.confirmBenefits.weNeedToVerify')}</p>
        <p className='mb-4'>
          {t('pages.confirmBenefits.verificationSentTo', {
            email: hashEmail(employeeData?.email),
          })}
        </p>

        {showResend && (
          <div className='text-center my-6'>
            <Button
              buttonType={ButtonType.secondary}
              onClick={resendEmail}
              disabled={resending}
            >
              {t('pages.confirmBenefits.resendEmail')}
            </Button>
          </div>
        )}

        <p className='text-sm'>
          {t('pages.confirmBenefits.checkSpamPrefix')}{' '}
          <a href='mailto:support@urbansitter.com' className='underline font-semibold'>
            support@urbansitter.com
          </a>
        </p>
      </div>
    </div>
  );
});
